package com.writerslab

import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import org.jetbrains.compose.web.css.AlignItems
import androidx.compose.runtime.setValue
import kotlinx.browser.localStorage
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

// ──────────────────────────────────────────────────────────────────────
// Localization helpers — direct ports from the previous Compose UI.
// `calcAiCost` lives in Models.kt.
// ──────────────────────────────────────────────────────────────────────

private fun Metric.name(s: Strings): String = when (this) {
    Metric.COMPLEXITY        -> s.metricComplexity
    Metric.VOCABULARY        -> s.metricVocabulary
    Metric.PACE              -> s.metricPace
    Metric.DIALOGUE          -> s.metricDialogue
    Metric.EXPRESSIVENESS    -> s.metricExpressiveness
    Metric.COHERENCE         -> s.metricCoherence
    Metric.ARC_VOLATILITY    -> s.metricArcVolatility
    Metric.VIVIDNESS         -> s.metricVividness
    Metric.PATHOS            -> s.metricPathos
    Metric.MORALITY          -> s.metricMorality
    Metric.AUTHOR_STANCE     -> s.metricAuthorStance
    Metric.VOICES            -> s.metricVoices
    Metric.FILLERS           -> s.metricFillers
    Metric.SENSORY           -> s.metricSensory
    Metric.ORIGINALITY       -> s.metricOriginality
    Metric.DIRECTNESS        -> s.metricDirectness
    Metric.VERB_STRENGTH     -> s.metricVerbStrength
    Metric.PRECISION         -> s.metricPrecision
    Metric.LIVELINESS        -> s.metricLiveliness
    Metric.RHYTHM            -> s.metricRhythm
    Metric.CLEAN_SYNTAX      -> s.metricCleanSyntax
    Metric.CEFR_RANGE        -> s.metricCefrRange
    Metric.RARE_VOCABULARY   -> s.metricRareVocabulary
    Metric.REGISTER_SHIFT    -> s.metricRegisterShift
    Metric.VOCABULARY_AGE    -> s.metricVocabularyAge
    Metric.STAGE_DIRECTIONS  -> s.metricStageDirections
    Metric.NO_REPETITION     -> s.metricNoRepetition
    Metric.DIRECT_POV        -> s.metricDirectPov
    Metric.PHRASAL_VERBS     -> s.metricPhrasalVerbs
    Metric.NO_NOMINALISATION -> s.metricNoNominalisation
    Metric.CONCRETENESS      -> s.metricConcreteness
}

private fun Metric.tip(s: Strings): String = when (this) {
    Metric.COMPLEXITY        -> s.tipComplexity
    Metric.VOCABULARY        -> s.tipVocabulary
    Metric.PACE              -> s.tipPace
    Metric.DIALOGUE          -> s.tipDialogue
    Metric.EXPRESSIVENESS    -> s.tipExpressiveness
    Metric.COHERENCE         -> s.tipCoherence
    Metric.ARC_VOLATILITY    -> s.tipArcVolatility
    Metric.VIVIDNESS         -> s.tipVividness
    Metric.PATHOS            -> s.tipPathos
    Metric.MORALITY          -> s.tipMorality
    Metric.AUTHOR_STANCE     -> s.tipAuthorStance
    Metric.VOICES            -> s.tipVoices
    Metric.FILLERS           -> s.tipFillers
    Metric.SENSORY           -> s.tipSensory
    Metric.ORIGINALITY       -> s.tipOriginality
    Metric.DIRECTNESS        -> s.tipDirectness
    Metric.VERB_STRENGTH     -> s.tipVerbStrength
    Metric.PRECISION         -> s.tipPrecision
    Metric.LIVELINESS        -> s.tipLiveliness
    Metric.RHYTHM            -> s.tipRhythm
    Metric.CLEAN_SYNTAX      -> s.tipCleanSyntax
    Metric.CEFR_RANGE        -> s.tipCefrRange
    Metric.RARE_VOCABULARY   -> s.tipRareVocabulary
    Metric.REGISTER_SHIFT    -> s.tipRegisterShift
    Metric.VOCABULARY_AGE    -> s.tipVocabularyAge
    Metric.STAGE_DIRECTIONS  -> s.tipStageDirections
    Metric.NO_REPETITION     -> s.tipNoRepetition
    Metric.DIRECT_POV        -> s.tipDirectPov
    Metric.PHRASAL_VERBS     -> s.tipPhrasalVerbs
    Metric.NO_NOMINALISATION -> s.tipNoNominalisation
    Metric.CONCRETENESS      -> s.tipConcreteness
}

/** "Good direction" for each metric on the 1-10 display scale —
 *  derived server-side from the LR coefficients (per language) and
 *  shipped via `result.qualityDirections`. Drives bar fill colour
 *  AND drag-flag eligibility, so both signals agree.
 *
 *   - HIGH: moving the value up (above the corpus mean) helps quality
 *   - LOW : moving it down helps quality
 *   - NONE: descriptive metric with no clear data-driven direction
 *           (small coefficient or LR doesn't include this metric) */
internal enum class GoodDirection { HIGH, LOW, NONE }

internal fun metricGoodDirection(result: AnalysisResult, metric: Metric): GoodDirection =
    when (result.qualityDirections[metric]) {
        "high" -> GoodDirection.HIGH
        "low" -> GoodDirection.LOW
        else -> GoodDirection.NONE
    }

/** Bar fill colour driven by `(value - reference)` in the metric's
 *  good direction.  Returns violet for descriptive metrics or the
 *  trivial value == reference case where the fill collapses to 0. */
private fun barFillColor(metric: MetricItem, direction: GoodDirection): String {
    if (direction == GoodDirection.NONE) return WLColors.Primary
    val ref = metric.reference ?: return WLColors.Primary
    val delta = metric.value - ref
    if (kotlin.math.abs(delta) < 0.01) return WLColors.Primary
    val movingUp = delta > 0
    val goodWhenUp = direction == GoodDirection.HIGH
    return if (movingUp == goodWhenUp) WLColors.Success else WLColors.Error
}

private fun POV.localized(s: Strings): String = when (this) {
    POV.FIRST   -> s.povFirst
    POV.THIRD   -> s.povThird
    POV.MIXED   -> s.povMixed
    POV.UNKNOWN -> s.povUnknown
}

private fun Mood.localized(s: Strings): String = when (this) {
    Mood.BRIGHT        -> s.moodBright
    Mood.RATHER_BRIGHT -> s.moodRatherBright
    Mood.NEUTRAL       -> s.moodNeutral
    Mood.RATHER_DARK   -> s.moodRatherDark
    Mood.DARK          -> s.moodDark
    Mood.HEAVY         -> s.moodHeavy
}

private fun Emotion.localized(s: Strings): String = when (this) {
    Emotion.ANGER        -> s.emotionAnger
    Emotion.ANTICIPATION -> s.emotionAnticipation
    Emotion.DISGUST      -> s.emotionDisgust
    Emotion.FEAR         -> s.emotionFear
    Emotion.JOY          -> s.emotionJoy
    Emotion.SADNESS      -> s.emotionSadness
    Emotion.SURPRISE     -> s.emotionSurprise
    Emotion.TRUST        -> s.emotionTrust
}

private fun Tag.localized(s: Strings): String = when (this) {
    Tag.ANTIQUITY         -> s.tagAntiquity
    Tag.MEDIEVAL          -> s.tagMedieval
    Tag.C19TH_CENTURY     -> s.tag19thCentury
    Tag.USSR              -> s.tagUssr
    Tag.WW2               -> s.tagWw2
    Tag.FUTURE            -> s.tagFuture
    Tag.SCHOOL_ACADEMY    -> s.tagSchoolAcademy
    Tag.SEA_SHIP          -> s.tagSeaShip
    Tag.SPACE             -> s.tagSpace
    Tag.PRISON            -> s.tagPrison
    Tag.CASTLE_PALACE     -> s.tagCastlePalace
    Tag.POST_APOCALYPSE   -> s.tagPostApocalypse
    Tag.DYSTOPIA          -> s.tagDystopia
    Tag.CYBERPUNK         -> s.tagCyberpunk
    Tag.STEAMPUNK         -> s.tagSteampunk
    Tag.WAR               -> s.tagWar
    Tag.INVESTIGATION     -> s.tagInvestigation
    Tag.MAGIC             -> s.tagMagic
    Tag.ROMANCE           -> s.tagRomance
    Tag.POLITICS_INTRIGUE -> s.tagPoliticsIntrigue
    Tag.SPORT             -> s.tagSport
    Tag.HEIST             -> s.tagHeist
    Tag.VAMPIRES          -> s.tagVampires
    Tag.WEREWOLVES        -> s.tagWerewolves
    Tag.DRAGONS           -> s.tagDragons
    Tag.ELVES             -> s.tagElves
    Tag.ZOMBIES           -> s.tagZombies
    Tag.DEMONS            -> s.tagDemons
    Tag.ISEKAI            -> s.tagIsekai
    Tag.LITRPG            -> s.tagLitrpg
    Tag.ACADEMY           -> s.tagAcademy
    Tag.APOCALYPSE        -> s.tagApocalypse
    Tag.CHOSEN_ONE        -> s.tagChosenOne
    Tag.DARK_LORD         -> s.tagDarkLord
    Tag.TOURNAMENT        -> s.tagTournament
    Tag.REVENGE           -> s.tagRevenge
    Tag.MERCENARY         -> s.tagMercenary
    Tag.ASCENSION         -> s.tagAscension
    Tag.ISEKAI_FEMALE     -> s.tagIsekaiFemale
    Tag.ENEMIES_TO_LOVERS -> s.tagEnemiesToLovers
    Tag.FAKE_MARRIAGE     -> s.tagFakeMarriage
    Tag.FORBIDDEN_LOVE    -> s.tagForbiddenLove
    Tag.SECOND_CHANCE     -> s.tagSecondChance
    Tag.DETECTIVE         -> s.tagDetective
    Tag.THRILLER          -> s.tagThriller
    Tag.SCI_FI            -> s.tagSciFi
    Tag.FANTASY           -> s.tagFantasy
    Tag.ACTION            -> s.tagAction
    Tag.SPY               -> s.tagSpy
    Tag.COMING_OF_AGE     -> s.tagComingOfAge
    Tag.REDEMPTION        -> s.tagRedemption
    Tag.LOSS_GRIEF        -> s.tagLossGrief
    Tag.POWER_TYRANNY     -> s.tagPowerTyranny
    Tag.LONELINESS        -> s.tagLoneliness
    Tag.COZY              -> s.tagCozy
    Tag.DARK              -> s.tagDark
    Tag.IRONIC            -> s.tagIronic
    Tag.NOSTALGIC         -> s.tagNostalgic
}

@Composable
private fun heroesText(count: Int): String = when {
    count == 0 -> LocalStrings.current.heroesNone
    count == 1 -> LocalStrings.current.heroesOne
    count <= 3 -> LocalStrings.current.heroesSeveral
    count <= 6 -> LocalStrings.current.heroesMany
    else       -> LocalStrings.current.heroesVeryMany
}

@Composable
private fun emotionsText(pcts: Map<Emotion, Double>): String {
    if (pcts.isEmpty()) return ""
    val sorted = pcts.entries.filter { it.value >= 13.0 }.sortedByDescending { it.value }
    val s = LocalStrings.current
    return sorted.joinToString(", ") { (k, v) -> "${k.localized(s)} ${v.toInt()}%" }
}

/** Estimate reading time in plain language: «4 ч 20 мин» / «35 мин».
 *  Uses ≈250 wpm — the average for adult readers on prose fiction in
 *  both Russian and English. Rounds minutes to the nearest 5 so the
 *  estimate doesn't pretend to be more precise than it is. */
@Composable
private fun formatReadingTime(words: Int): String {
    val s = LocalStrings.current
    if (words <= 0) return ""
    val totalMin = (words / 250.0).toInt()
    val rounded = ((totalMin + 2) / 5) * 5  // round to nearest 5 min
    val h = rounded / 60
    val m = rounded % 60
    return when {
        h <= 0 -> "${m.coerceAtLeast(5)} ${s.minutesShort}"
        m == 0 -> "$h ${s.hoursShort}"
        else   -> "$h ${s.hoursShort} $m ${s.minutesShort}"
    }
}

private fun formatNumber(n: Int): String {
    val s = n.toString()
    val sb = StringBuilder()
    for ((i, c) in s.reversed().withIndex()) {
        if (i > 0 && i % 3 == 0) sb.append(' ')
        sb.append(c)
    }
    return sb.reverse().toString()
}

// ──────────────────────────────────────────────────────────────────────
// Components
// ──────────────────────────────────────────────────────────────────────

@Composable
fun ResultsArea(
    result: AnalysisResult,
    bookFilename: String?,
    bookId: String?,
    isLoggedIn: Boolean,
    onRequireAuth: () -> Unit,
    /** Editing the text in the reader; `editedAt` is filled from [result]. */
    textEditing: TextEditing,
    aiSidebarSlot: @Composable () -> Unit = {},
    aiSlot: @Composable () -> Unit,
    promoSlot: @Composable () -> Unit = {},
) {
    val s = LocalStrings.current
    val unsupportedLanguage = result.language == null || result.language == Language.OTHER

    if (unsupportedLanguage) {
        Div({ style { maxW(800); flexCol(gapPx = 12) } }) {
            UnsupportedLanguageCard(bookId)
        }
        return
    }

    // Shared promo title + author state — lives at this level so the
    // sidebar PromoSettingsCard and the main-column PromoTabs read the
    // same holder via LocalPromoSettings. Keyed on bookId so switching
    // books resets the inputs (matches the previous remember-in-PromoTabs
    // behaviour where `key(vm.bookId)` did the reset). PromoSettings
    // also rehydrates from localStorage so a page refresh — or closing
    // and re-opening the browser — doesn't wipe what the user typed
    // (bug #22).
    val promoSettings = remember(bookId) { PromoSettings(bookId) }
    CompositionLocalProvider(LocalPromoSettings provides promoSettings) {
        // 1. Static analysis — framed block: sidebar (stats / strengths /
        //    mood+genre) on the left, reliability warning + detailed metrics on
        //    the right. The frame owns the title + collapse; metrics render bare.
        CollapsibleBlock(
            title = s.blockStatic,
            persistKey = "static",
            defaultExpanded = false,
            subtitle = s.blockStaticSub,
            help = {
                BlockHelp(s.helpMetricsTitle, s.helpMetricsBody, BlockKind.STATIC,
                    id = AnalyticsEvents.HelpBlock.METRICS)
            },
            collapsedPreview = { MetricsChipStrip(result) },
        ) {
            Div({ classes("wl-block-grid") }) {
                Div({ classes("wl-sidebar-stack") }) {
                    Div({ classes("wl-sb-quality") }) {
                        QualityHeader(result, bookFilename)
                    }
                    Div({ classes("wl-sb-highlights") }) {
                        HighlightsCard(result)
                    }
                    Div({ classes("wl-sb-profile") }) {
                        BookProfileBlock(
                            mood = result.mood?.localized(s) ?: s.heroesNone,
                            emotionPcts = result.emotionPcts,
                            tags = result.tags.map { it.localized(s) },
                        )
                    }
                }
                Div({ style { flexCol(gapPx = 16); property("min-width", "0") } }) {
                    DismissibleReliabilityWarning(result)
                    ExpandableMetrics(result, bare = true)
                }
            }
        }
        // Anonymous, right under the static-analysis block: prominent "sign in
        // for the welcome bonus" banner (credits + free generations).
        if (!isLoggedIn) {
            SignupBonusBanner(onSignIn = onRequireAuth)
        }
        // 2. Manuscript reader with issue highlighting — framed; legend-left /
        //    text-right via `.wl-ms-top` (order-swaps on narrow).
        CollapsibleBlock(title = s.blockManuscript, persistKey = "manuscript", subtitle = s.blockManuscriptSub) {
            ManuscriptSection(bookId, result.language, isLoggedIn, onRequireAuth, textEditing)
        }
        // 3. AI (deep LLM) analysis — framed: the AI score card (or the run-CTA
        //    before it's run) on the left, the full analysis on the right.
        CollapsibleBlock(
            title = s.blockAi,
            persistKey = "ai",
            subtitle = s.blockAiSub,
            help = {
                BlockHelp(s.helpAiInsightsTitle, s.helpAiInsightsBody, BlockKind.AI,
                    id = AnalyticsEvents.HelpBlock.AI_INSIGHTS)
            },
        ) {
            Div({ classes("wl-block-grid") }) {
                Div({ style { flexCol(gapPx = 12) } }) { aiSidebarSlot() }
                Div({ style { flexCol(gapPx = 16); property("min-width", "0") } }) {
                    aiSlot()
                }
            }
        }
        // 4. Promo materials — framed: settings on the left, generated
        //    materials on the right.
        CollapsibleBlock(
            title = s.blockPromo,
            persistKey = "promo",
            subtitle = s.blockPromoSub,
            help = {
                BlockHelp(s.helpPromoMaterialsTitle, s.helpPromoMaterialsBody, BlockKind.AI,
                    id = AnalyticsEvents.HelpBlock.PROMO_MATERIALS)
            },
        ) {
            Div({ classes("wl-block-grid") }) {
                Div({ style { flexCol(gapPx = 12) } }) { PromoSettingsCard() }
                Div({ style { flexCol(gapPx = 16); property("min-width", "0") } }) {
                    promoSlot()
                }
            }
        }
    }
}

/** Persisted per-block collapse state (localStorage), keyed by the block. */
private fun loadBlockExpanded(key: String, default: Boolean): Boolean =
    when (localStorage.getItem("wl_block_$key")) {
        "1" -> true
        "0" -> false
        else -> default
    }

private fun saveBlockExpanded(key: String, value: Boolean) {
    localStorage.setItem("wl_block_$key", if (value) "1" else "0")
}

/** Frame around a whole result section (both columns): a bordered card whose
 *  title bar collapses the entire block. The block owns the header + collapse,
 *  so the inner right-column components render bare (without their own headers).
 *  `subtitle` sits under the title (always visible, incl. collapsed).
 *  `collapsedPreview` renders in place of the content when collapsed.
 *  Collapse state persists in localStorage under `persistKey`. */
@Composable
private fun CollapsibleBlock(
    title: String,
    persistKey: String,
    defaultExpanded: Boolean = true,
    subtitle: String? = null,
    help: (@Composable () -> Unit)? = null,
    collapsedPreview: (@Composable () -> Unit)? = null,
    content: @Composable () -> Unit,
) {
    var expanded by remember(persistKey) {
        mutableStateOf(loadBlockExpanded(persistKey, defaultExpanded))
    }
    Div({ classes("wl-block-frame") }) {
        Div({ classes("wl-block-head") }) {
            Div({
                classes("wl-block-head-main")
                onClick { expanded = !expanded; saveBlockExpanded(persistKey, expanded) }
            }) {
                Span({ classes("wl-block-caret") }) { Text(if (expanded) "▾" else "▸") }
                Div({ classes("wl-block-titles") }) {
                    Span({ classes("wl-block-title") }) { Text(title) }
                    if (subtitle != null) {
                        Span({ classes("wl-block-sub") }) { Text(subtitle) }
                    }
                }
            }
            help?.invoke()
        }
        if (expanded) {
            Div({ classes("wl-block-body") }) { content() }
        } else if (collapsedPreview != null) {
            Div({ classes("wl-block-preview") }) { collapsedPreview() }
        }
    }
}

/** Compact one-row preview of the quality metrics (boosts first, then drags,
 *  then the rest) — the static block's collapsed state; mirrors what the old
 *  detailed-metrics block showed when collapsed. */
@Composable
private fun MetricsChipStrip(result: AnalysisResult) {
    val weakDrags = findQualityDrags(result)
    val boosts = findQualityBoosts(result)
    val directions: (Metric) -> GoodDirection = { metricGoodDirection(result, it) }
    val candidates = result.profile + result.character + result.style
    val boostStrip = candidates.filter { it.id in boosts }
        .sortedByDescending { it.contribution ?: 0.0 }
    val dragStrip = candidates.filter { it.id in weakDrags && it.id !in boosts }
        .sortedBy { it.contribution ?: 0.0 }
    val restStrip = candidates.filter { it.id !in boosts && it.id !in weakDrags }
    val sortedMetrics = boostStrip + dragStrip + restStrip
    Div({
        classes("wl-no-scrollbar")
        style {
            property("display", "flex")
            property("flex-direction", "row")
            property("gap", "6px")
            property("overflow-x", "auto")
        }
    }) {
        sortedMetrics.forEach {
            CompactMetricChip(
                it,
                dragDirection = weakDrags[it.id],
                direction = directions(it.id),
                isBoost = it.id in boosts,
            )
        }
    }
}

@Composable
private fun UnsupportedLanguageCard(bookId: String?) {
    val s = LocalStrings.current
    var sample by remember { mutableStateOf<String?>(null) }
    LaunchedEffect(bookId) {
        sample = bookId?.let { BookRepository.getTextSample(it) }
    }
    Div({
        style {
            box(bgColor = WLColors.SurfaceContainerLow, radiusPx = 8, paddingPx = 20)
            flexCol(gapPx = 10)
            fillWidth()
            property("box-sizing", "border-box")
        }
    }) {
        Span({ style { typography(TextLevel.BodyMedium); fg(WLColors.Error) } }) {
            Text(s.unsupportedLanguageMessage)
        }
        sample?.takeIf { it.isNotBlank() }?.let { sampleText ->
            Span({ style { typography(TextLevel.LabelSmall); fg(WLColors.OnSurfaceVariant) } }) {
                Text(s.unsupportedLanguageSampleHeader)
            }
            Div({
                style {
                    box(bgColor = WLColors.SurfaceContainerHigh, radiusPx = 8, paddingPx = 12)
                    fillWidth()
                    property("max-height", "200px")
                    property("overflow-y", "auto")
                    property("white-space", "pre-wrap")
                    property("word-break", "break-word")
                    property("box-sizing", "border-box")
                    typography(TextLevel.BodySmall)
                    fg(WLColors.OnSurfaceVariant)
                }
            }) { Text(sampleText) }
        }
    }
}

@Composable
private fun QualityHeader(result: AnalysisResult, bookFilename: String?) {
    val s = LocalStrings.current
    val q = result.quality
    val qColor = when {
        q == null  -> WLColors.OnSurfaceVariant
        q >= 7     -> WLColors.Success
        q >= 4     -> "#FACC15"
        else       -> WLColors.Error
    }
    Div({
        classes("wl-tip-host")
        attr("tabindex", "0")
        style {
            bg(WLColors.SurfaceContainerLow)
            property("border-radius", "8px")
            property("padding", "24px 24px 24px 20px")
            property("border-left", "4px solid ${WLColors.Primary}")
            fillWidth()
            flexCol(gapPx = 8)
            property("box-sizing", "border-box")
            property("position", "relative")
        }
    }) {
        BlockHelpCorner(s.helpQualityTitle, s.helpQualityBody, BlockKind.STATIC, id = AnalyticsEvents.HelpBlock.QUALITY)
        // Filename at the top — moved here from the top bar. Identifies
        // which book the score below belongs to. word-break to handle
        // long undersore-separated names like `vern_vokrug_sveta_80_dney.fb2`
        // without overflowing the 300px sidebar column.
        if (!bookFilename.isNullOrBlank()) {
            Span({
                style {
                    typography(TextLevel.BodySmall)
                    fg(WLColors.OnSurfaceVariant)
                    property("word-break", "break-all")
                    property("padding-bottom", "4px")
                }
            }) { Text(bookFilename) }
        }
        Div({ style { flexRow(gapPx = 8); property("align-items", "flex-end"); property("flex-wrap", "wrap") } }) {
            Span({
                classes("wl-quality-number")
                style {
                    property("font-size", "36px")
                    property("font-weight", "600")
                    property("line-height", "1")
                    fg(qColor)
                }
            }) { Text(q?.toString() ?: "—") }
            Span({
                style {
                    typography(TextLevel.BodySmall)
                    fg(WLColors.OnSurfaceVariant)
                    property("padding-bottom", "3px")
                }
            }) { Text("/10") }
            // Тихая подпись «откуда взялся балл» — напоминание, что это
            // лингво-статистика, а не оценочное суждение. На низких
            // баллах вердиктная «Слабый стиль» обижала автора; здесь —
            // нейтральный ярлык, тот же текст, что в модалке справки.
            Span({
                style {
                    typography(TextLevel.LabelSmall)
                    fg(WLColors.Primary)
                    property("text-transform", "uppercase")
                    property("letter-spacing", "0.6px")
                    property("font-weight", "600")
                    property("padding-bottom", "6px")
                    property("padding-left", "10px")
                }
            }) { Text(s.blockKindStatic) }
        }
        Div({ style { flexCol(gapPx = 4) } }) {
            MetaRow(s.words, formatNumber(result.words))
            MetaRow(s.charsLabel, formatNumber(result.chars))
            MetaRow(s.readingTimeLabel, formatReadingTime(result.words))
            MetaRow(s.povLabel, (result.pov ?: POV.UNKNOWN).localized(s))
            MetaRow(s.charactersLabel, heroesText(result.charactersCount))
        }
        // Caveat — the score's known biases (translated / historical /
        // non-fiction prose). Relates to the SCORE only, so it lives here on
        // the quality card, not on the whole static block's header.
        Span({
            style {
                typography(TextLevel.LabelSmall)
                fg(WLColors.Outline)
                property("padding-top", "4px")
            }
        }) { Text(s.qualityCaveat) }
        TooltipBubble(s.qualityHint)
    }
}

@Composable
private fun MetaRow(label: String, value: String) {
    Div({ style { flexRow(gapPx = 8) } }) {
        Span({ style { typography(TextLevel.BodySmall); fg(WLColors.OnSurfaceVariant) } }) { Text(label) }
        Span({ style { typography(TextLevel.BodySmall); fg(WLColors.OnSurface) } }) { Text(value) }
    }
}

/** Top strengths + top weaknesses surfaced as a compact sidebar card.
 *  Pulls signed contributions from `result.qualityContributions` —
 *  positive ones boost the overall style score, negative ones drag it —
 *  takes the two biggest in each direction and labels them by metric
 *  name. Gives the writer an at-a-glance read on «what's working» and
 *  «what's not» without having to expand the full metrics grid. */
@Composable
private fun HighlightsCard(result: AnalysisResult) {
    val s = LocalStrings.current
    val contribs = result.qualityContributions
        .filterValues { kotlin.math.abs(it) > 0.0001 }
    if (contribs.isEmpty()) return
    val sorted = contribs.entries.sortedByDescending { it.value }
    val strengths = sorted.filter { it.value > 0 }.take(2).map { it.key }
    val weaknesses = sorted.filter { it.value < 0 }.takeLast(2).reversed().map { it.key }
    if (strengths.isEmpty() && weaknesses.isEmpty()) return
    Div({
        style {
            bg(WLColors.SurfaceContainerLow)
            property("border-radius", "8px")
            property("padding", "20px 20px 20px 16px")
            property("border-left", "4px solid ${WLColors.Primary}")
            flexCol(gapPx = 12)
            fillWidth()
            property("box-sizing", "border-box")
            property("position", "relative")
        }
    }) {
        BlockHelpCorner(s.helpHighlightsTitle, s.helpHighlightsBody, BlockKind.STATIC, id = AnalyticsEvents.HelpBlock.HIGHLIGHTS)
        if (strengths.isNotEmpty()) {
            HighlightsRow(s.keyStrengths, strengths.map { it.name(s) }, WLColors.Success)
        }
        if (weaknesses.isNotEmpty()) {
            HighlightsRow(s.weakSpots, weaknesses.map { it.name(s) }, WLColors.Error)
        }
    }
}

@Composable
private fun HighlightsRow(label: String, items: List<String>, dotColor: String) {
    Div({ style { flexCol(gapPx = 4) } }) {
        Div({ style { flexRow(gapPx = 6, align = AlignItems.Center) } }) {
            // Small colour dot — green for strengths, red for weak spots.
            Div({
                style {
                    bg(dotColor)
                    property("width", "8px")
                    property("height", "8px")
                    property("border-radius", "50%")
                    property("flex-shrink", "0")
                }
            })
            Span({
                style {
                    typography(TextLevel.LabelSmall)
                    fg(WLColors.OnSurfaceVariant)
                    property("text-transform", "uppercase")
                    property("letter-spacing", "0.4px")
                }
            }) { Text(label) }
        }
        Span({
            style {
                typography(TextLevel.BodyMedium)
                fg(WLColors.OnSurface)
            }
        }) { Text(items.joinToString(" · ")) }
    }
}

/** Plutchik-ish emotion → hex colour mapping, picked to read on a dark
 *  background. Joy carries the gold accent, fear/sadness sit cool,
 *  anger/disgust hot. Used by the emotion stacked-bar in the book-profile
 *  card to give the book a fingerprint at a glance. */
private fun emotionColor(emotion: Emotion): String = when (emotion) {
    Emotion.JOY          -> "#F5B947"  // gold
    Emotion.SADNESS      -> "#6B9EF7"  // cool blue
    Emotion.ANGER        -> "#F47072"  // red
    Emotion.FEAR         -> "#8E5CB8"  // deep purple
    Emotion.DISGUST      -> "#A3A03B"  // mustard
    Emotion.SURPRISE     -> "#4FB8C9"  // cyan
    Emotion.ANTICIPATION -> "#E89351"  // orange
    Emotion.TRUST        -> "#6FCF97"  // green
}

/** Sidebar metadata card combining mood + emotions + genre tags into
 *  one «Book profile» panel. Replaces the previous two separate cards
 *  (MoodBlock + a bare TagsRow): the metadata is the same kind of
 *  «what kind of book is this» information, no reason to split it
 *  into two sidebar slots. Tags render inline only when present. */
@Composable
private fun BookProfileBlock(
    mood: String,
    emotionPcts: Map<Emotion, Double>,
    tags: List<String>,
) {
    val s = LocalStrings.current
    Div({
        style {
            bg(WLColors.SurfaceContainerLow)
            property("border-radius", "8px")
            property("padding", "20px 20px 20px 16px")
            property("border-left", "4px solid ${WLColors.Primary}")
            flexCol(gapPx = 12)
            fillWidth()
            property("box-sizing", "border-box")
            property("position", "relative")
        }
    }) {
        BlockHelpCorner(s.helpBookProfileTitle, s.helpBookProfileBody, BlockKind.STATIC, id = AnalyticsEvents.HelpBlock.BOOK_PROFILE)
        Div({ style { flexCol(gapPx = 4) } }) {
            Span({
                style { typography(TextLevel.BodySmall); fg(WLColors.OnSurfaceVariant) }
            }) { Text(s.moodLabel) }
            Span({
                style { typography(TextLevel.TitleMedium); fg(WLColors.OnSurface) }
            }) { Text(mood) }
        }
        // Emotion stacked-bar: each emotion shows as a coloured segment
        // sized by its % of the chunk-level emotion mix. Legend underneath
        // names *every* contributor present in the bar so no colour stays
        // a mystery — the bar isn't a teaser, it's the legend visualised.
        // Top contributors first; the long tail wraps onto extra rows.
        // Only rendered when at least one emotion clears 1%.
        if (emotionPcts.isNotEmpty()) {
            val sorted = emotionPcts.entries
                .filter { it.value >= 1.0 }
                .sortedByDescending { it.value }
            if (sorted.isNotEmpty()) {
                val total = sorted.sumOf { it.value }.coerceAtLeast(1.0)
                Div({
                    style {
                        property("display", "flex")
                        fillWidth()
                        property("height", "8px")
                        property("border-radius", "6px")
                        property("overflow", "hidden")
                        bg(WLColors.SurfaceContainerHigh)
                    }
                }) {
                    sorted.forEach { (emo, pct) ->
                        Div({
                            attr("title", "${emo.localized(s)} ${pct.toInt()}%")
                            style {
                                property("flex", "${pct / total}")
                                bg(emotionColor(emo))
                            }
                        })
                    }
                }
                Div({
                    style {
                        property("display", "flex")
                        property("flex-wrap", "wrap")
                        property("gap", "4px 14px")
                    }
                }) {
                    sorted.forEach { (emo, pct) ->
                        EmotionLegendItem(emo, pct, s)
                    }
                }
            }
        }
        if (tags.isNotEmpty()) {
            Div({
                style {
                    property("display", "flex")
                    property("flex-wrap", "wrap")
                    property("gap", "6px")
                    property("padding-top", "2px")
                }
            }) {
                tags.forEach { tag ->
                    Span({
                        style {
                            typography(TextLevel.LabelSmall)
                            fg(WLColors.Primary)
                            property("background-color", "rgba(165, 174, 247, 0.12)")
                            property("border-radius", "120px")
                            property("padding", "5px 10px")
                            property("white-space", "nowrap")
                        }
                    }) { Text(tag) }
                }
            }
        }
    }
}

@Composable
private fun EmotionLegendItem(emo: Emotion, pct: Double, s: Strings) {
    Div({ style { flexRow(gapPx = 6, align = AlignItems.Center) } }) {
        Div({
            style {
                bg(emotionColor(emo))
                property("width", "8px")
                property("height", "8px")
                property("border-radius", "50%")
                property("flex-shrink", "0")
            }
        })
        Span({
            style {
                typography(TextLevel.LabelSmall)
                fg(WLColors.OnSurface)
            }
        }) { Text("${emo.localized(s)} ${pct.toInt()}%") }
    }
}

@Composable
private fun ExpandableMetrics(result: AnalysisResult, bare: Boolean = false) {
    val s = LocalStrings.current
    val analytics = LocalAnalyticsEvents.current
    // `bare` = rendered inside a framed CollapsibleBlock: drop this component's
    // own card/header/chevron/chip-strip and always show the full metric cards
    // (the frame owns the title + collapse).
    var expanded by remember { mutableStateOf(bare) }
    // Computed once and reused by both views: drag flags drive the red
    // border on collapsed chips AND the red ring on expanded cards.
    val weakDrags = findQualityDrags(result)
    val boosts = findQualityBoosts(result)
    // Per-metric "good direction" — derived server-side from LR coefs
    // (per language) and shipped as `qualityDirections`. Looked up by
    // both bar fill colour and drag-flag eligibility so they agree.
    val directions: (Metric) -> GoodDirection = { metricGoodDirection(result, it) }
    // Collapsed-strip order: green-bordered boosts first (what's working,
    // sorted by biggest positive contribution), then red-bordered drags
    // (what to fix, biggest pull first), then the rest in section order.
    // Splitting explicitly into three groups + concatenating is clearer
    // than a multi-key sort and guarantees we don't leave a "no-border"
    // gap between the highlighted clusters.
    val candidates = result.profile + result.character + result.style
    val boostStrip = candidates.filter { it.id in boosts }
        .sortedByDescending { it.contribution ?: 0.0 }
    val dragStrip = candidates.filter { it.id in weakDrags && it.id !in boosts }
        .sortedBy { it.contribution ?: 0.0 }
    val restStrip = candidates.filter { it.id !in boosts && it.id !in weakDrags }
    val sortedMetrics = boostStrip + dragStrip + restStrip

    Div({
        style {
            if (!bare) {
                bg(WLColors.SurfaceContainerLow)
                property("border-radius", "8px")
                property("border-left", "4px solid ${WLColors.Primary}")
            }
            fillWidth()
            property("box-sizing", "border-box")
            // Intentionally NO `overflow: hidden` here — tooltip bubbles
            // (absolutely positioned) on inner cards need to escape this
            // container. The divider lines below use horizontal margins so
            // they don't poke past the rounded corners.
        }
    }) {
        if (!bare) {
        // Header — clickable to expand/collapse. Trailing slot holds the
        // per-block help "?". Static-analysis re-runs are now driven by
        // a backend schema-version check, so there is no manual rerun
        // button here anymore.
        Div({
            style {
                flexRow(gapPx = 8, align = AlignItems.Center)
                property("justify-content", "space-between")
                property("padding", "14px 20px 14px 16px")
            }
        }) {
            Div({
                style {
                    flexRow(gapPx = 12, align = AlignItems.FlexStart)
                    property("flex", "1")
                    property("min-width", "0")
                    clickableCursor()
                }
                onClick {
                    if (!expanded) analytics.metricsBlockExpanded()
                    expanded = !expanded
                }
            }) {
                // Chevron — единый стиль с PromoMaterialsSection и
                // AiInsightsSection: текстовый ▸/▾, Violet100, 16px width.
                Span({
                    style {
                        fg(WLColors.Primary)
                        typography(TextLevel.TitleMedium)
                        property("width", "16px")
                        property("flex-shrink", "0")
                        property("padding-top", "2px")
                    }
                }) { Text(if (expanded) "▾" else "▸") }
                Div({ style { flexCol(gapPx = 2); property("min-width", "0") } }) {
                    Span({
                        style {
                            typography(TextLevel.HeadlineSmall)
                            fg(WLColors.Primary)
                        }
                    }) {
                        Text(s.detailedMetrics)
                    }
                    // Subtitle: «not AI, only form, target = mass-reader
                    // bestsellers». Sits permanently under the title so
                    // a new user doesn't mistake static metrics for the
                    // AI deep-analysis surface below. Two paragraphs,
                    // separated by «\n\n» in the string — pre-line keeps
                    // the break, collapses internal whitespace as usual.
                    Span({
                        style {
                            typography(TextLevel.BodySmall)
                            fg(WLColors.OnSurfaceVariant)
                            property("white-space", "pre-line")
                            property("padding-top", "2px")
                        }
                    }) {
                        Text(s.detailedMetricsExplain)
                    }
                }
            }
            BlockHelp(s.helpMetricsTitle, s.helpMetricsBody, BlockKind.STATIC, id = AnalyticsEvents.HelpBlock.METRICS)
        }

        Div({
            style {
                property("height", "1px")
                property("background-color", "rgba(74, 71, 84, 0.5)")
                property("margin", "0 8px 0 4px")
            }
        })
        }

        // `key(expanded)` forces the inner Div to remount when the toggle
        // flips, which retriggers the fade-in animation. No height transition
        // — `overflow: hidden` (needed for height clipping) would clip tooltip
        // bubbles on metric cards.
        androidx.compose.runtime.key(expanded) {
            Div({ classes("wl-fade-in") }) {
                if (!expanded) {
                    Div({
                        classes("wl-no-scrollbar")
                        style {
                            property("display", "flex")
                            property("flex-direction", "row")
                            property("gap", "6px")
                            property("padding", "10px 16px 12px 12px")
                            property("overflow-x", "auto")
                            clickableCursor()
                        }
                        onClick {
                            if (!expanded) {
                                analytics.metricsBlockExpanded()
                                expanded = true
                            }
                        }
                    }) {
                        sortedMetrics.forEach {
                            CompactMetricChip(
                                it,
                                dragDirection = weakDrags[it.id],
                                direction = directions(it.id),
                                isBoost = it.id in boosts,
                            )
                        }
                    }
                } else {
                    Div({
                        style {
                            flexCol(gapPx = 16)
                            property("padding", "12px 20px 16px 16px")
                        }
                    }) {
                        // Drag-summary banner — only shown when at least
                        // one metric tripped the drag threshold.
                        if (weakDrags.isNotEmpty()) {
                            Div({
                                style {
                                    box(bgColor = "rgba(244, 112, 114, 0.08)", radiusPx = 8, paddingPx = 12)
                                    fillWidth()
                                    flexRow(gapPx = 10, align = AlignItems.FlexStart)
                                    property("box-sizing", "border-box")
                                    border("1px", "solid", "rgba(244, 112, 114, 0.28)")
                                }
                            }) {
                                Span({
                                    style {
                                        property("font-size", "14px")
                                        property("line-height", "1")
                                        fg(WLColors.Error)
                                        property("flex-shrink", "0")
                                        property("padding-top", "2px")
                                    }
                                }) { Text("ⓘ") }
                                Span({
                                    style {
                                        typography(TextLevel.BodySmall)
                                        fg(WLColors.OnSurfaceVariant)
                                    }
                                }) { Text(s.weakMetricsHint) }
                            }
                        }
                        MetricsSection(
                            s.sectionProfile, s.sectionProfileHint,
                            result.profile, qualityBar = false,
                            weakDrags = weakDrags, directions = directions,
                        )
                        MetricsSection(
                            s.sectionNarrative, s.sectionNarrativeHint,
                            result.character, qualityBar = false,
                            weakDrags = weakDrags, directions = directions,
                        )
                        MetricsSection(
                            s.sectionStyle, s.sectionStyleHint,
                            result.style, qualityBar = true,
                            weakDrags = weakDrags, directions = directions,
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun CompactMetricChip(
    metric: MetricItem,
    dragDirection: DragDirection? = null,
    direction: GoodDirection = GoodDirection.NONE,
    /** Mirror of `dragDirection != null` on the positive side — when set,
     *  the chip gets a green outline + glow to flag «this metric is
     *  pulling overall quality up». Boost beats drag if both are set
     *  (shouldn't happen by construction). */
    isBoost: Boolean = false,
) {
    val s = LocalStrings.current
    val isWeak = !isBoost && dragDirection != null
    Div({
        style {
            flexRow(gapPx = 8)
            bg(WLColors.SurfaceContainerHigh)
            property("border-radius", "8px")
            property("padding", "8px 10px")
            property("flex-shrink", "0")
            when {
                isBoost -> {
                    property("border", "1px solid ${WLColors.Success}")
                    property("box-shadow", "0 0 0 2px rgba(111, 207, 151, 0.12)")
                }
                isWeak -> {
                    property("border", "1px solid ${WLColors.Error}")
                    property("box-shadow", "0 0 0 2px rgba(244, 112, 114, 0.12)")
                }
            }
        }
    }) {
        Span({ style { typography(TextLevel.LabelSmall); fg(WLColors.OnSurfaceVariant) } }) {
            Text(metric.id.name(s))
        }
        Span({ style { typography(TextLevel.TitleSmall); fg(WLColors.OnSurface) } }) {
            Text("${metric.value}")
        }
        // 40px-wide compact variant of the same delta-bar used on the
        // expanded card; keeps red/green/violet semantics consistent.
        Div({
            style {
                property("width", "40px")
                property("flex-shrink", "0")
            }
        }) {
            MetricDeltaBar(metric, direction, heightPx = 3, tickPx = 1)
        }
    }
}

@Composable
private fun MetricsSection(
    title: String,
    hint: String,
    metrics: List<MetricItem>,
    qualityBar: Boolean,
    weakDrags: Map<Metric, DragDirection> = emptyMap(),
    directions: (Metric) -> GoodDirection = { GoodDirection.NONE },
) {
    Div({ style { flexCol(gapPx = 4) } }) {
        Span({
            style {
                typography(TextLevel.LabelLarge)
                fg(WLColors.OnSurfaceVariant)
                property("text-transform", "uppercase")
            }
        }) { Text(title) }
        Span({
            style {
                typography(TextLevel.BodySmall)
                fg(WLColors.OnSurfaceVariant)
                property("padding-bottom", "6px")
            }
        }) { Text(hint) }
        Div({
            style {
                property("display", "grid")
                property("grid-template-columns", if (metrics.size <= 6) "repeat(3, 1fr)" else "repeat(4, 1fr)")
                property("gap", "8px")
            }
        }) {
            metrics.forEach {
                MetricCard(
                    it, qualityBar,
                    dragDirection = weakDrags[it.id],
                    direction = directions(it.id),
                )
            }
        }
    }
}

@Composable
private fun MetricCard(
    metric: MetricItem,
    qualityBar: Boolean,
    dragDirection: DragDirection? = null,
    direction: GoodDirection = GoodDirection.NONE,
) {
    val s = LocalStrings.current
    val name = metric.id.name(s)
    val tooltip = metric.id.tip(s)
    val isWeak = dragDirection != null

    Div({
        classes("wl-tip-host")
        attr("tabindex", "0")
        style {
            bg(WLColors.SurfaceContainerHigh)
            property("border-radius", "8px")
            property("padding", "12px")
            flexCol(gapPx = 6)
            property("align-items", "center")
            if (isWeak) {
                property("border", "1px solid ${WLColors.Error}")
                property("box-shadow", "0 0 0 3px rgba(244, 112, 114, 0.12)")
            }
        }
    }) {
        Span({ style { typography(TextLevel.LabelSmall); fg(WLColors.OnSurfaceVariant) } }) { Text(name) }
        Span({ style { typography(TextLevel.HeadlineSmall); fg(WLColors.OnSurface) } }) { Text("${metric.value}") }
        MetricDeltaBar(metric, direction, heightPx = 4, tickPx = 2)
        TooltipBubble(tooltip)
    }
}

/** Wrapper composable: shows the yellow ⚠ reliability banner if the
 *  text is below the calibration thresholds AND the user hasn't
 *  dismissed it yet for this session. State is held in a per-instance
 *  `remember` — the banner reappears on page reload (deliberate; the
 *  underlying problem hasn't changed). Once dismissed, the layout
 *  collapses and ExpandableMetrics moves up. */
@Composable
private fun DismissibleReliabilityWarning(result: AnalysisResult) {
    if (result.metricsReliable && result.styleReliable) return
    val s = LocalStrings.current
    var dismissed by remember(result) { mutableStateOf(false) }
    if (dismissed) return
    val message = if (!result.metricsReliable) s.shortTextWarningSevere
                  else s.shortTextWarningModerate
    ReliabilityWarning(message, onDismiss = { dismissed = true })
}

/** Yellow alert banner shown above the metric grid when the input
 *  text is shorter than the calibration thresholds (5K words for
 *  basic metrics, 30K for style). Distinct from the red drag-summary
 *  banner so the two warnings don't compete for attention. The close
 *  button on the right hides the banner for the rest of the session. */
@Composable
private fun ReliabilityWarning(message: String, onDismiss: (() -> Unit)? = null) {
    Div({
        style {
            box(bgColor = "rgba(244, 196, 112, 0.10)", radiusPx = 8, paddingPx = 14)
            fillWidth()
            flexRow(gapPx = 10, align = AlignItems.FlexStart)
            property("box-sizing", "border-box")
            border("1px", "solid", "rgba(244, 196, 112, 0.40)")
        }
    }) {
        Span({
            style {
                property("font-size", "16px")
                property("line-height", "1")
                fg("#F4C470")
                property("flex-shrink", "0")
                property("padding-top", "1px")
            }
        }) { Text("\u26A0") }
        Span({
            style {
                typography(TextLevel.BodyMedium)
                fg(WLColors.OnSurface)
                property("flex", "1")
            }
        }) { Text(message) }
        if (onDismiss != null) {
            Div({
                attr("role", "button")
                attr("aria-label", "Close")
                style {
                    fg(WLColors.OnSurfaceVariant)
                    property("font-size", "18px")
                    property("line-height", "1")
                    property("flex-shrink", "0")
                    property("padding", "0 4px")
                    clickableCursor()
                }
                onClick { onDismiss() }
            }) { Text("\u00D7") }
        }
    }
}

/** Bar that shows `value` relative to `reference` (LR corpus mean) —
 *  fills the segment between the two with red/green/violet depending
 *  on whether the deviation moves in the metric's good direction.
 *  Falls back to a 0-to-value violet fill if no reference is shipped
 *  (uncalibrated language pack / older API). */
@Composable
private fun MetricDeltaBar(
    metric: MetricItem,
    direction: GoodDirection,
    heightPx: Int,
    tickPx: Int,
) {
    Div({
        style {
            property("width", "100%")
            property("height", "${heightPx}px")
            property("background-color", WLColors.Surface)
            property("border-radius", "2px")
            property("position", "relative")
        }
    }) {
        val ref = metric.reference
        // Descriptive metrics (no data-driven direction) and any
        // missing-reference fallbacks render the legacy 0→value violet
        // fill, no tick. Showing a tick without a direction promises a
        // "good/bad" signal we don't actually have.
        if (ref == null || direction == GoodDirection.NONE) {
            Div({
                style {
                    property("height", "100%")
                    property("width", "${metric.value * 10}%")
                    property("background-color", WLColors.Primary)
                    property("border-radius", "2px")
                }
            })
            return@Div
        }
        val refPct = (ref.coerceIn(0.0, 10.0) / 10.0 * 100.0)
        val valPct = (metric.value.toDouble().coerceIn(0.0, 10.0) / 10.0 * 100.0)
        val left = kotlin.math.min(refPct, valPct)
        val width = kotlin.math.abs(valPct - refPct)
        if (width > 0.01) {
            Div({
                style {
                    property("position", "absolute")
                    property("top", "0")
                    property("bottom", "0")
                    property("left", "${left}%")
                    property("width", "${width}%")
                    property("background-color", barFillColor(metric, direction))
                    property("border-radius", "2px")
                }
            })
        }
        // Reference tick — sits on top of any fill that touches it.
        Div({
            style {
                property("position", "absolute")
                property("left", "${refPct}%")
                property("top", "-2px")
                property("bottom", "-2px")
                property("width", "${tickPx}px")
                property("background-color", "rgba(237, 237, 237, 0.85)")
                property("border-radius", "1px")
                property("transform", "translateX(-50%)")
            }
        })
    }
}
